import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Inbox, LogOut, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { JobsManager } from "@/components/admin/JobsManager";
import { InquiriesManager } from "@/components/admin/InquiriesManager";
import { SettingsManager } from "@/components/admin/SettingsManager";

export const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);

  const refreshStats = () => api.get("/admin/stats").then((res) => setStats(res.data)).catch(() => {});
  useEffect(() => { refreshStats(); }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/assets/prgl-logo.png" alt="PRGL" className="h-8 w-auto" />
            <span className="text-sm font-semibold text-slate-500 uppercase tracking-widest hidden sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500 hidden sm:inline" data-testid="admin-user-email">{user?.email}</span>
            <Button asChild variant="outline" size="sm" data-testid="admin-view-site-btn">
              <Link to="/">View site</Link>
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout-btn" className="text-slate-600">
              <LogOut size={16} className="mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
            {[
              ["New inquiries", stats.new_inquiries, "text-cyan-600"],
              ["Total inquiries", stats.inquiries, "text-slate-900"],
              ["Active jobs", stats.active_jobs, "text-blue-600"],
              ["Total jobs", stats.jobs, "text-slate-900"],
            ].map(([label, value, color]) => (
              <div key={label} className="bg-white border border-slate-200 rounded-lg p-5">
                <p className={`text-3xl font-bold tracking-tight ${color}`}>{value}</p>
                <p className="text-sm text-slate-500 mt-1">{label}</p>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="inquiries">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="inquiries" data-testid="admin-tab-inquiries"><Inbox size={15} className="mr-1.5" /> Inquiries</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="admin-tab-jobs"><Briefcase size={15} className="mr-1.5" /> Jobs</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings"><Settings2 size={15} className="mr-1.5" /> Site Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="inquiries" className="mt-6">
            <InquiriesManager onChanged={refreshStats} />
          </TabsContent>
          <TabsContent value="jobs" className="mt-6">
            <JobsManager onChanged={refreshStats} />
          </TabsContent>
          <TabsContent value="settings" className="mt-6">
            <SettingsManager />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};
